import React, { Component } from "react"
import dynamic from "next/dynamic"
import { inject, observer } from "mobx-react"
import styled, { ThemeProvider } from "styled-components"
import { Row, Col, Grid } from "react-styled-flexboxgrid"


import WinItButton from "~/components/WinItButton"
import Input from "~/components/Form/LandingInput"
import FormError from "~/components/Form/ErrorText"
import ZipCodeInput from "~/components/traffic/lookup/Forms/Elements/ZipCodeInput"
import UserInfoInputs from "~/components/traffic/lookup/Forms/UserInfoInputs"
import LastNameDOBLicenseLicenseState from "~/components/traffic/lookup/Forms/Elements/LastNameDOBLicenseLicenseState"
import flexBoxGridTheme from "~/components/traffic/lookup/Forms/UserInfoInputs/flexBoxGridTheme"
import Last4SSNSecure from "~/components/traffic/lookup/Forms/Elements/Last4SSNSecure"
import Spacer from "~/components/Spacer"
import {
  NYDMV_SHARED_STEP1_KEYS,
  NYDMV_SUBMIT_KEYS_NY,
  NYDMV_SUBMIT_KEYS_NON_NY,
} from "~/stores/trafficTicket/lookupConfig"
import TRACKING from "~/services/tracking"
import withTranslations from "~/components/_hocs/withTranslations"

const LicenseScan = dynamic(
  () => import("~/components/traffic/lookup/LicenseScan"),
  { ssr: false }
)
const DOCUMENT_NUMBER_HELP_TABS = [
  { labelKey: "nydmv.documentNumberHelp.tabAfter2017", filenames: ["doc-num-help-1", "doc-num-help-2", "doc-num-help-3"] },
  { labelKey: "nydmv.documentNumberHelp.tabBefore2017", filenames: ["doc-num-help-4"] },
  { labelKey: "nydmv.documentNumberHelp.tabBefore2014", filenames: ["doc-num-help-5", "doc-num-help-6"] },
]
const DOCUMENT_NUMBER_IMAGE_BASE = "/static/images/document-number-help"

const Form = styled.form`
  -webkit-appearance: none;
  width: 100%;
  min-width: 0;
  margin: 12px 0;
`

const StepContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;

  @media (max-width: 480px) {
    gap: 16px;
  }
`

const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 4px;
`

const StepDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(props) =>
    props.$active || props.$completed
      ? props.theme.colors.primary
      : "transparent"};
  border: 1.5px solid
    ${(props) =>
      props.$active || props.$completed
        ? props.theme.colors.primary
        : "rgba(0, 0, 0, 0.2)"};
  transition: background 0.2s ease, border-color 0.2s ease;
`

const StepLabel = styled.span`
  font-family: ${(props) => props.theme.fonts.avenirNextMedium};
  font-size: 0.8rem;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #6c6d6e;
`

const FieldsRow = styled.div`
  display: grid;
  grid-template-columns: ${(p) => (p.$cols === 2 ? "1fr 1fr" : "1fr 1fr 1fr")};
  gap: 20px 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const FieldWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

/** Spacer that drops the inline Search button so it aligns with the input
 *  fields (mirrors Nassau's SearchFormContainer button column). Hidden on
 *  small-desktop / mobile where inputs stack. */
const ButtonDiv = styled.div`
  height: 52px;
  ${(props) => props.theme.media.smallDesktop`
    display: none;
  `}
`

const FieldLabel = styled.label`
  font-family: ${(props) => props.theme.fonts.avenirNextMedium};
  font-size: 0.9375rem;
  height: 21px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  color: #374553;
`

const RequiredMark = styled.span`
  color: #e53935;
  font-size: 0.9em;
`

const ButtonRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: center;
  margin-top: 8px;
  flex-wrap: wrap;
`

const ScanZone = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  min-width: 0;
  min-height: 180px;
  padding: 28px 24px;
  background: #fff;
  border: 2px dashed ${(p) => p.theme?.colors?.primary || '#3089f3'};
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(48, 137, 243, 0.04);
  }

  @media (max-width: 480px) {
    min-height: 120px;
    padding: 16px 12px;
    gap: 8px;
  }

  @media (max-width: 360px) {
    min-height: 100px;
    padding: 12px 10px;
    gap: 6px;
  }
`

const ScanZoneIcon = styled.div`
  width: 56px;
  height: 56px;
  flex-shrink: 0;
  color: ${(p) => p.theme?.colors?.primary || '#3089f3'};
  display: flex;
  align-items: center;
  justify-content: center;

  & svg {
    width: 100%;
    height: 100%;
  }

  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
  }

  @media (max-width: 360px) {
    width: 32px;
    height: 32px;
  }
`

const ScanZoneTitle = styled.span`
  font-family: ${(p) => p.theme?.fonts?.avenirNextDemi || 'inherit'};
  font-size: 1.125rem;
  color: ${(p) => p.theme?.colors?.primary || '#3089f3'};
  text-align: center;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;

  @media (max-width: 480px) {
    font-size: 1rem;
  }

  @media (max-width: 360px) {
    font-size: 0.9375rem;
  }
`

const ScanZoneHint = styled.span`
  font-size: 0.875rem;
  color: #6c6d6e;
  text-align: center;
  max-width: 100%;

  @media (max-width: 480px) {
    font-size: 0.8125rem;
  }

  @media (max-width: 360px) {
    font-size: 0.75rem;
  }
`

const RetryScanWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`

const RetryScanBtn = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  font-size: 0.875rem;
  font-family: ${(p) => p.theme?.fonts?.avenirNextMedium || 'inherit'};
  color: ${(p) => p.theme?.colors?.primary || '#3089f3'};
  background: transparent;
  border: 1px dashed ${(p) => p.theme?.colors?.primary || '#3089f3'};
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    background: rgba(48, 137, 243, 0.06);
  }
`

const RetryScanIcon = styled.span`
  width: 20px;
  height: 20px;
  display: inline-flex;
  color: inherit;
`

const ScanRequiredHint = styled.p`
  font-size: 0.9375rem;
  color: #374553;
  margin: 0;
  line-height: 1.4;
`

// Step 2 — License photos (full design)
const Step2Block = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
`

const Step2IconCircle = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 2px solid rgba(48, 137, 243, 0.35);
  background: rgba(48, 137, 243, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
`

const Step2Title = styled.h2`
  font-family: ${(p) => p.theme?.fonts?.avenirNextDemi || 'inherit'};
  font-size: 1.25rem;
  font-weight: bold;
  color: #1a237e;
  margin: 0 0 8px 0;
  line-height: 1.3;
`

const Step2Description = styled.p`
  font-size: 0.8125rem;
  color: #6c6d6e;
  margin: 0 0 16px 0;
  line-height: 1.45;
  max-width: 320px;
`

const Step2StepsList = styled.ol`
  list-style: none;
  padding: 0;
  margin: 0 0 16px 0;
  width: 100%;
  max-width: 320px;
  text-align: left;
`

const Step2StepItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
`

const Step2StepNum = styled.span`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${(p) => p.theme?.colors?.primary || '#3089f3'};
  color: #fff;
  font-family: ${(p) => p.theme?.fonts?.avenirNextDemi || 'inherit'};
  font-size: 12px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const Step2StepContent = styled.div`
  flex: 1;
  min-width: 0;
`

const Step2StepTitle = styled.div`
  font-family: ${(p) => p.theme?.fonts?.avenirNextDemi || 'inherit'};
  font-size: 0.8125rem;
  font-weight: bold;
  color: #1a237e;
  margin-bottom: 0;
  line-height: 1.5;
`

const Step2StepHint = styled.div`
  font-size: 0.8125rem;
  color: #6c6d6e;
  line-height: 1.35;
`

const Step2PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  max-width: 320px;
  height: 52px;
  padding: 0 24px;
  background: ${(p) => p.theme?.colors?.primary || '#3089f3'};
  color: #fff;
  font-family: ${(p) => p.theme?.fonts?.avenirNextDemi || 'inherit'};
  font-size: 1rem;
  font-weight: bold;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.92;
  }
  &:active {
    opacity: 0.88;
  }
`

const Step2SkipLink = styled.button`
  margin-top: 12px;
  padding: 0;
  background: none;
  border: none;
  font-size: 0.875rem;
  color: #5b9bd5;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    text-decoration: none;
    color: ${(p) => p.theme?.colors?.primary || '#3089f3'};
  }
`

const WidgetInput = styled(Input)`
  max-width: 100%;
  height: 70px;

  @media (max-width: 768px) {
    height: 56px;
  }

  @media (max-width: 480px) {
    height: 48px;
  }
`

const DocHelpSection = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #eee;
`

const DocHelpTabsRow = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`

const DocHelpTab = styled.button`
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid ${(p) => (p.$active ? p.theme.colors.primary : "rgba(0, 0, 0, 0.15)")};
  background: ${(p) => (p.$active ? "rgba(33, 150, 243, 0.08)" : "#fff")};
  color: ${(p) => (p.$active ? p.theme.colors.primary : p.theme.colors.text.main)};
  font-family: ${(p) => p.theme.fonts.avenirNextMedium};
  font-size: 0.8rem;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  &:hover {
    border-color: ${(p) => p.theme.colors.primary};
    background: rgba(33, 150, 243, 0.05);
  }
`

const DocHelpImagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`

const DocHelpImageBtn = styled.button`
  padding: 0;
  border: none;
  background: #f5f5f5;
  border-radius: 8px;
  cursor: pointer;
  display: block;
  width: 100%;
  text-align: center;
  overflow: hidden;
  &:focus { outline: 2px solid ${(p) => p.theme.colors.primary}; outline-offset: 2px; }
`

const DocHelpImage = styled.img`
  width: 100%;
  max-width: 220px;
  height: auto;
  object-fit: contain;
  display: block;
  margin: 0 auto;
  vertical-align: middle;
`

const DocHelpImagePlaceholder = styled.div`
  max-width: 220px;
  margin: 0 auto;
  aspect-ratio: 1;
  background: #f5f5f5;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  color: ${(p) => p.theme.colors.text.subContent};
  padding: 8px;
  text-align: center;
`

const DocHelpFullscreenOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  cursor: pointer;
`

const DocHelpFullscreenImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  pointer-events: none;
`

const DocHelpFullscreenClose = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 40px;
  height: 40px;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  font-size: 1.5rem;
  line-height: 1;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: rgba(255, 255, 255, 0.25); }
`

const scrollToTop = () =>
  process.browser && window.scrollTo({ top: 0, behavior: "smooth" })

const scanIconSvg = (size = 56) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M3 7V5a2 2 0 012-2h2" />
    <path d="M17 3h2a2 2 0 012 2v2" />
    <path d="M21 17v2a2 2 0 01-2 2h-2" />
    <path d="M7 21H5a2 2 0 01-2-2v-2" />
    <path d="M7 12h10" />
  </svg>
)

const licenseCardIconSvg = (width = 40, height = 26) => (
  <svg viewBox="0 0 56 36" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width, height }}>
    <rect x="2" y="2" width="52" height="32" rx="3" />
    <rect x="6" y="8" width="44" height="12" rx="1" />
  </svg>
)

const scanFrameIconSvg = (size = 24) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M3 3v6M3 3h6M21 3h-6M21 3v6M3 21v-6M3 21h6M21 21h-6M21 21v-6" />
  </svg>
)

const arrowRightSvg = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
)

@inject((all) => ({
  isNYSLicense: all.store.trafficLookup.isNYSLicense,
  licenseBack: all.store.trafficLookup.licenseBack,
  changeLicenseBack: all.store.trafficLookup.changeLicenseBack,
  trafficTicketNumber: all.store.trafficLookup.trafficTicketNumber,
  violateDate: all.store.trafficLookup.violateDate,
  changeTrafficTicketNumber: all.store.trafficLookup.changeTrafficTicketNumber,
  changeViolateDate: all.store.trafficLookup.changeViolateDate,
  getFetcherTickets: all.store.trafficLookup.getFetcherTickets,
  validateDisplayMessage: all.store.trafficLookup.validateDisplayMessage,
  validateKeyList: all.store.trafficLookup.validateKeyList,
  validateKeys: all.store.trafficLookup.validateKeys,
  addTouched: all.store.trafficLookup.addTouched,
  savePartialSearchCriteria: all.store.trafficLookup.savePartialSearchCriteria,
  fetcherName: all.store.trafficLookup.fetcherName,
  useNYDMVForm: all.store.trafficLookup.useNYDMVForm,
  hasSentFetcherInfoAdded: all.store.trafficLookup.hasSentFetcherInfoAdded,
  markFetcherInfoAdded: all.store.trafficLookup.markFetcherInfoAdded,
  fetcherState: all.store.trafficLookup.fetcherState,
  isMobile: all.store.ui.isMobile,
}))
@observer
class NYDMVForm extends Component {
  state = {
    step: 1,
    showDocumentNumberFallback: false,
    docHelpTabIndex: 0,
    docHelpImageErrors: {},
    docHelpFullscreenSrc: null,
  }

  /** GA: multi-step wizard only; not used on single-page lookup flows. */
  reportLookupStep = step => {
    if (!this.props.useNYDMVForm) return
    TRACKING.traffic.lookupStepView({
      fetcherName: this.props.fetcherName,
      state: this.props.fetcherState,
      step,
    })
  }

  componentDidMount() {
    this.reportLookupStep(this.state.step)
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.isNYSLicense !== this.props.isNYSLicense) {
      this.setState({ step: 1 })
      return
    }
    if (prevState.step !== this.state.step) {
      this.reportLookupStep(this.state.step)
    }
  }

  handleNextStep = (e) => {
    e.preventDefault()
    NYDMV_SHARED_STEP1_KEYS.forEach((key) => this.props.addTouched(key))
    if (!this.props.validateKeyList(NYDMV_SHARED_STEP1_KEYS)) {
      if (this.props.useNYDMVForm && !this.props.hasSentFetcherInfoAdded) {
        TRACKING.traffic.fetcherInfoAdded(this.props.fetcherName)
        this.props.markFetcherInfoAdded()
      }
      if (this.props.useNYDMVForm) {
        this.props.savePartialSearchCriteria('step1')
      }
      this.setState({ step: 2 })
      scrollToTop()
    }
  }

  handleStep2Next = (e) => {
    e.preventDefault()
    if (this.props.isNYSLicense) {
      this.props.addTouched("licenseBack")
      if (this.props.validateKeyList(["licenseBack"])) {
        this.setState({ showDocumentNumberFallback: true })
        return
      }
    }
    if (this.props.useNYDMVForm) {
      this.props.savePartialSearchCriteria('step2')
    }
    this.goToStep3()
  }

  handleBackStep = (e) => {
    e.preventDefault()
    this.setState((s) => ({ step: s.step - 1 }))
    scrollToTop()
  }

  goToStep3 = () => {
    this.setState({ step: 3 })
    scrollToTop()
  }

  handleStep3Submit = (e) => {
    e.preventDefault()
    const submitKeys = this.props.isNYSLicense ? NYDMV_SUBMIT_KEYS_NY : NYDMV_SUBMIT_KEYS_NON_NY
    submitKeys.forEach((k) => this.props.addTouched(k))
    if (!this.props.validateKeyList(submitKeys)) {
      this.props.getFetcherTickets("traffic")
    }
  }

  // ——— Step 1 (shared): your info + DOB + license/state; no scan (SSN moved to step 3)
  renderSharedStep1() {
    return (
      <StepContent>
        <StepIndicator>
          <StepDot $active />
          <StepDot />
          <StepDot />
          <StepLabel>Step 1 of 3 — Your info</StepLabel>
        </StepIndicator>

        <UserInfoInputs />

        <ThemeProvider theme={flexBoxGridTheme}>
          <Grid style={{ width: "100%" }}>
            <Row>
              <Col lg={11} md={12} sm={13} xs={16}>
                <Spacer mobile={15} desktop={15} />
                <LastNameDOBLicenseLicenseState />
              </Col>
              <Col lg={3} md={3} sm={3} xs={16}>
                <ButtonDiv />
                <WinItButton
                  isMobile={this.props.isMobile}
                  width="100%"
                  height={70}
                  maxWidth={this.props.isMobile ? "none" : "170px"}
                  style={{ fontSize: "1rem" }}
                  type="button"
                  onClick={this.handleNextStep}
                  disabled={!!this.props.validateKeys(NYDMV_SHARED_STEP1_KEYS)}
                >
                  {this.props.translate("nydmv.searchTicketsButton")}
                </WinItButton>
              </Col>
            </Row>
          </Grid>
        </ThemeProvider>
      </StepContent>
    )
  }

  // ——— Step 2: License photos — full design (icon, steps, Scan button, Skip link, footer)
  renderScanStep2() {
    const { isMobile, licenseBack, changeLicenseBack, validateDisplayMessage } = this.props
    const licenseBackError = validateDisplayMessage("licenseBack")
    const showDocFallback = this.state.showDocumentNumberFallback
    const t = this.props.translate

    const handleSkip = () => {
      if (this.props.isNYSLicense) {
        this.setState({ showDocumentNumberFallback: true })
        this.props.addTouched("licenseBack")
      } else {
        this.goToStep3()
      }
    }

    const scanTrigger = (
      <Step2PrimaryBtn type="button">
        {scanFrameIconSvg(22)}
        <span>{t("nydmv.scanStep.scanButton")}</span>
        {arrowRightSvg()}
      </Step2PrimaryBtn>
    )

    const retryScanTrigger = (
      <RetryScanWrap>
        <RetryScanBtn>
          <RetryScanIcon>{scanIconSvg(20)}</RetryScanIcon>
          {t("nydmv.scanStep.retryScan")}
        </RetryScanBtn>
      </RetryScanWrap>
    )

    const descriptionText = t("nydmv.scanStep.description")
    const descriptionParts = descriptionText.split(/\*\*(.*?)\*\*/g)

    return (
      <StepContent>
        <StepIndicator>
          <StepDot $completed />
          <StepDot $active />
          <StepDot />
          <StepLabel style={{ textTransform: "uppercase" }}>{t("nydmv.scanStep.stepLabel")}</StepLabel>
        </StepIndicator>

        {showDocFallback && (
          <FieldWrap style={{ width: "100%", maxWidth: 400, margin: "0 auto 24px" }}>
            <FieldLabel>
              {t("nydmv.licenseBack.label")}{" "}
              <RequiredMark>*</RequiredMark>
            </FieldLabel>
            <WidgetInput
              placeholder={t("nydmv.licenseBack.placeholder")}
              value={licenseBack}
              onChange={(e) => changeLicenseBack(e.target.value)}
              error={licenseBackError}
              type="text"
              maxLength={20}
            />
            <FormError error={licenseBackError} />
            <DocHelpSection>
              <DocHelpTabsRow>
                {DOCUMENT_NUMBER_HELP_TABS.map((tab, index) => (
                  <DocHelpTab
                    key={tab.labelKey}
                    type="button"
                    $active={this.state.docHelpTabIndex === index}
                    onClick={() => this.setState({ docHelpTabIndex: index, docHelpImageErrors: {} })}
                  >
                    {t(tab.labelKey)}
                  </DocHelpTab>
                ))}
              </DocHelpTabsRow>
              <DocHelpImagesGrid>
                {DOCUMENT_NUMBER_HELP_TABS[this.state.docHelpTabIndex].filenames.map((filename) => {
                  const src = `${DOCUMENT_NUMBER_IMAGE_BASE}/${filename}.png`
                  return this.state.docHelpImageErrors[filename] ? (
                    <DocHelpImagePlaceholder key={filename}>
                      {t("nydmv.documentNumberHelp.imageNotFound")}
                    </DocHelpImagePlaceholder>
                  ) : (
                    <DocHelpImageBtn
                      key={filename}
                      type="button"
                      onClick={() => this.setState({ docHelpFullscreenSrc: src })}
                    >
                      <DocHelpImage
                        src={src}
                        alt={t(DOCUMENT_NUMBER_HELP_TABS[this.state.docHelpTabIndex].labelKey)}
                        onError={() =>
                          this.setState((s) => ({
                            docHelpImageErrors: { ...s.docHelpImageErrors, [filename]: true },
                          }))
                        }
                      />
                    </DocHelpImageBtn>
                  )
                })}
              </DocHelpImagesGrid>
            </DocHelpSection>
            {this.state.docHelpFullscreenSrc && (
              <DocHelpFullscreenOverlay
                onClick={() => this.setState({ docHelpFullscreenSrc: null })}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Escape" && this.setState({ docHelpFullscreenSrc: null })}
                aria-label="Close"
              >
                <DocHelpFullscreenClose
                  type="button"
                  onClick={(e) => { e.stopPropagation(); this.setState({ docHelpFullscreenSrc: null }) }}
                  aria-label="Close"
                >
                  ×
                </DocHelpFullscreenClose>
                <DocHelpFullscreenImage
                  src={this.state.docHelpFullscreenSrc}
                  alt=""
                  onClick={(e) => e.stopPropagation()}
                />
              </DocHelpFullscreenOverlay>
            )}
          </FieldWrap>
        )}

        {!showDocFallback && (
          <Step2Block>
            <Step2IconCircle>
              <span style={{ color: "#1a237e" }}>{licenseCardIconSvg()}</span>
            </Step2IconCircle>
            <Step2Title>{t("nydmv.scanStep.title")}</Step2Title>
            <Step2Description>
              {descriptionParts.map((part, i) =>
                i % 2 === 1 ? <strong key={i}>{part}</strong> : part
              )}
            </Step2Description>
            <Step2StepsList>
              <Step2StepItem>
                <Step2StepNum>1</Step2StepNum>
                <Step2StepContent>
                  <Step2StepTitle>{t("nydmv.scanStep.step1Title")}</Step2StepTitle>
                  <Step2StepHint>{t("nydmv.scanStep.step1Hint")}</Step2StepHint>
                </Step2StepContent>
              </Step2StepItem>
              <Step2StepItem>
                <Step2StepNum>2</Step2StepNum>
                <Step2StepContent>
                  <Step2StepTitle>{t("nydmv.scanStep.step2Title")}</Step2StepTitle>
                  <Step2StepHint>{t("nydmv.scanStep.step2Hint")}</Step2StepHint>
                </Step2StepContent>
              </Step2StepItem>
              <Step2StepItem>
                <Step2StepNum>3</Step2StepNum>
                <Step2StepContent>
                  <Step2StepTitle>{t("nydmv.scanStep.step3Title")}</Step2StepTitle>
                  <Step2StepHint>{t("nydmv.scanStep.step3Hint")}</Step2StepHint>
                </Step2StepContent>
              </Step2StepItem>
            </Step2StepsList>

            <LicenseScan
              onCantScan={() => {
                if (this.props.isNYSLicense) {
                  this.setState({ showDocumentNumberFallback: true })
                } else {
                  this.goToStep3()
                }
              }}
              onScanSuccess={this.goToStep3}
              cantScanLabel={t("nydmv.scanStep.cantScan")}
              couldntScanLabel={t("nydmv.scanStep.couldntScan")}
            >
              {scanTrigger}
            </LicenseScan>

            <Step2SkipLink type="button" onClick={handleSkip}>
              {t("nydmv.scanStep.skipLink")}
            </Step2SkipLink>
          </Step2Block>
        )}

        {showDocFallback && (
          <LicenseScan
            onCantScan={() => {}}
            onScanSuccess={this.goToStep3}
            couldntScanLabel={t("nydmv.scanStep.couldntScan")}
          >
            {retryScanTrigger}
          </LicenseScan>
        )}

        <ButtonRow>
          {showDocFallback && (
            <WinItButton
              isMobile={isMobile}
              width="calc(50% - 6px)"
              height={50}
              type="button"
              onClick={this.handleStep2Next}
              disabled={this.props.isNYSLicense && !!this.props.validateKeys(["licenseBack"])}
            >
              {t("nydmv.searchTicketsButton")}
            </WinItButton>
          )}
          <WinItButton
            isMobile={isMobile}
            width={showDocFallback ? "calc(50% - 6px)" : "100%"}
            height={50}
            color="gray"
            type="button"
            onClick={this.handleBackStep}
          >
            Back
          </WinItButton>
        </ButtonRow>
      </StepContent>
    )
  }

  // ——— Step 3: ZIP + ticket ID + violation date (optional/required by NY vs non-NY) + last 4 SSN
  renderStep3() {
    const { isNYSLicense, isMobile } = this.props
    const submitKeys = isNYSLicense ? NYDMV_SUBMIT_KEYS_NY : NYDMV_SUBMIT_KEYS_NON_NY
    const ticketError = isNYSLicense ? null : this.props.validateDisplayMessage("trafficTicketNumber")
    const violateDateError = isNYSLicense ? null : this.props.validateDisplayMessage("violateDate")
    const t = this.props.translate

    return (
      <StepContent>
        <StepIndicator>
          <StepDot $completed />
          <StepDot $completed />
          <StepDot $active />
          <StepLabel>Step 3 of 3 — Ticket & location</StepLabel>
        </StepIndicator>

        <FieldsRow>
          <FieldWrap>
            <FieldLabel>
              {t("nydmv.step3.zipLabel")}{" "}
              <RequiredMark>*</RequiredMark>
            </FieldLabel>
            <ZipCodeInput />
          </FieldWrap>
          <FieldWrap>
            <FieldLabel>
              {t("nydmv.step3.ticketLabel")}{" "}
              {!isNYSLicense ? <RequiredMark>*</RequiredMark> : null}
            </FieldLabel>
            <WidgetInput
              placeholder="Ex: ABC1234567"
              value={this.props.trafficTicketNumber}
              onChange={(e) =>
                this.props.changeTrafficTicketNumber(e.target.value)
              }
              error={ticketError}
            />
            <FormError error={ticketError} />
          </FieldWrap>
          <FieldWrap>
            <FieldLabel>
              {t("nydmv.step3.violationDateLabel")}{" "}
              {!isNYSLicense ? <RequiredMark>*</RequiredMark> : null}
            </FieldLabel>
            <WidgetInput
              placeholder="MM/DD/YYYY"
              value={this.props.violateDate}
              onChange={(e) => this.props.changeViolateDate(e.target.value)}
              error={violateDateError}
              type="tel"
            />
            <FormError error={violateDateError} />
          </FieldWrap>
        </FieldsRow>

        <Last4SSNSecure />

        <ButtonRow>
          <WinItButton
            isMobile={isMobile}
            width="calc(50% - 6px)"
            height={50}
            type="submit"
            disabled={!!this.props.validateKeys(submitKeys)}
          >
            {t("nydmv.searchTicketsButton")}
          </WinItButton>
          <WinItButton
            isMobile={isMobile}
            width="calc(50% - 6px)"
            height={50}
            color="gray"
            type="button"
            onClick={this.handleBackStep}
          >
            Back
          </WinItButton>
        </ButtonRow>
      </StepContent>
    )
  }

  render() {
    const step = this.state.step
    const formSubmit = step === 1 ? this.handleNextStep : step === 2 ? this.handleStep2Next : this.handleStep3Submit
    const stepContent = step === 1 ? this.renderSharedStep1() : step === 2 ? this.renderScanStep2() : this.renderStep3()

    return (
      <>
        <Form onSubmit={(e) => { e.preventDefault(); formSubmit(e) }}>
          {stepContent}
        </Form>
      </>
    )
  }
}

export default withTranslations(NYDMVForm, "form.traffic")